import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthModule';
import AdminToolsBackToolbar from './AdminToolsBackToolbar';

const API = process.env.REACT_APP_API_URL || '';

const GAME_TYPE_LABEL = {
  trivia: 'Trivia',
  riddle: 'Arvoitus',
  abcd: 'ABCD',
  emoji: 'Emoji',
};

function MatchDetails({ s }) {
  const gt = String(s.gameType || '').toLowerCase();
  if (!s.matches || s.matches.length === 0) {
    return <p className="Admin-trivia-suggestions-nomatch">Ei sanatason osumia nykyiseen pankkiin.</p>;
  }
  const summary =
    gt === 'abcd'
      ? `Sanatason osumat pankissa (${s.bankFile || 'abcd.json'})`
      : gt === 'emoji'
        ? `Sanatason osumat pankissa (${s.bankFile || 'emojis.json'})`
        : gt === 'riddle'
          ? `Sanatason osumat pankissa (${s.bankFile || 'riddles.json'})`
          : `Sanatason osumat pankissa (${s.bankFile || 'questions.json'})`;

  return (
    <details className="Admin-trivia-suggestions-matches">
      <summary className="Admin-trivia-suggestions-matches-summary">
        {summary}
        <span className="Admin-trivia-suggestions-matches-count"> ({s.matches.length})</span>
      </summary>
      <ul className="Admin-trivia-suggestions-matches-list" aria-label="Mahdolliset osumat pankissa">
        {s.matches.map((m) => (
          <li key={m.id}>
            <span className="Admin-trivia-suggestions-match-id">{m.id}</span>
            {gt === 'abcd' ? (
              <>
                <div>{m.question}</div>
                <div className="Admin-trivia-suggestions-patterns">
                  A: {m.options?.A} · B: {m.options?.B} · C: {m.options?.C} · D: {m.options?.D}
                </div>
                <div>Oikea: {m.correctOption}</div>
              </>
            ) : gt === 'emoji' ? (
              <>
                <div className="Admin-trivia-suggestions-patterns">HTML/emoji: {m.emojis}</div>
                <div>{m.question}</div>
                <div className="Admin-trivia-suggestions-patterns">
                  Vastaukset: {(Array.isArray(m.answer) ? m.answer : []).join(', ')}
                </div>
              </>
            ) : (
              <>
                <div>{m.prompt}</div>
                <div className="Admin-trivia-suggestions-patterns">
                  Vastausmallit: {(m.answerPatterns || []).join(', ')}
                </div>
              </>
            )}
            <div className="Admin-trivia-suggestions-words">
              Yhteiset sanat: {m.matchingWords.join(', ')}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

export default function AdminTriviaSuggestionsPage() {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);

  const load = useCallback(() => {
    setError(null);
    setStatusMsg(null);
    const userIdStr = String(user.id).replace(/n$/, '');
    setLoading(true);
    fetch(`${API}/api/admin/trivia-suggestions/pending?userId=${encodeURIComponent(userIdStr)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Virhe');
        return data;
      })
      .then((data) => {
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      })
      .catch((err) => setError(err.message || 'Virhe'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (user && user.role === 'admin') load();
  }, [user, load]);

  if (!user) {
    return (
      <div className="App-block Admin-tools-page">
        <AdminToolsBackToolbar />
        <p>Kirjaudu sisään.</p>
      </div>
    );
  }

  if (user.role !== 'admin') {
    return (
      <div className="App-block Admin-tools-page">
        <AdminToolsBackToolbar />
        <p>Tämä sivu on vain ylläpitäjille.</p>
      </div>
    );
  }

  const review = (id, action) => {
    setError(null);
    setStatusMsg(null);
    const userIdStr = String(user.id).replace(/n$/, '');
    setActingId(id);
    fetch(`${API}/api/admin/trivia-suggestions/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: userIdStr, action }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || data.message || 'Virhe');
        return data;
      })
      .then((data) => {
        if (data.note) setStatusMsg(data.note);
        else if (data.refundedPc)
          setStatusMsg(`Hyvitetty ${data.refundedPc} PC lähettäjälle (syy: duplikaatti).`);
        else setStatusMsg('Tallennettu.');
        load();
      })
      .catch((err) => setError(err.message || 'Virhe'))
      .finally(() => setActingId(null));
  };

  return (
    <div className="App-block Admin-tools-page">
      <AdminToolsBackToolbar />

      <h1 className="Admin-tools-title">Peliehdotukset</h1>

      <p className="Admin-trivia-suggestions-intro">
        Näet ehdotukset, jotka lähettäjä on täyttänyt ja joihin ei ole vielä reagoitu (<code>seen = false</code>
        , <code>submitted = true</code>). Jäsenet ostavat oikeuden krediittikaupasta; kiitosviesti DM:ssä sisältää
        linkin lomakkeeseen. Sanatason osumat lasketaan pelityypin mukaan tiedostoista{' '}
        <code>questions.json</code>, <code>riddles.json</code>, <code>abcd.json</code> tai{' '}
        <code>emojis.json</code>.
      </p>

      <div className="Admin-trivia-suggestions-toolbar">
        <button type="button" className="Button" onClick={load} disabled={loading}>
          {loading ? 'Ladataan…' : 'Päivitä'}
        </button>
      </div>

      {error ? (
        <p className="Admin-daily-question-error" role="alert">
          {error}
        </p>
      ) : null}
      {statusMsg ? (
        <p className="Admin-daily-question-status" role="status">
          {statusMsg}
        </p>
      ) : null}

      {loading && suggestions.length === 0 ? <p>Ladataan…</p> : null}

      {!loading && suggestions.length === 0 ? <p>Ei avoimia ehdotuksia.</p> : null}

      <ul className="Admin-trivia-suggestions-list">
        {suggestions.map((s) => {
          const gt = String(s.gameType || '').toLowerCase();
          const typeLabel = GAME_TYPE_LABEL[gt] || s.gameType;
          return (
            <li key={s.id} className="Admin-trivia-suggestions-card">
              <div className="Admin-trivia-suggestions-card-head">
                <span className="Admin-trivia-suggestions-author">{s.authorLabel}</span>
                <span className="Admin-trivia-suggestions-id">
                  {typeLabel} · <code>{s.id}</code>
                </span>
              </div>
              {gt === 'abcd' && s.abcdOptions ? (
                <>
                  <p className="Admin-trivia-suggestions-q">
                    <strong>Kysymys:</strong> {s.question}
                  </p>
                  <p className="Admin-trivia-suggestions-a">
                    <strong>Vaihtoehdot:</strong> A: {s.abcdOptions.A} · B: {s.abcdOptions.B} · C:{' '}
                    {s.abcdOptions.C} · D: {s.abcdOptions.D}
                  </p>
                  <p className="Admin-trivia-suggestions-a">
                    <strong>Oikea:</strong> {s.answer}
                  </p>
                </>
              ) : gt === 'emoji' ? (
                <>
                  <p className="Admin-trivia-suggestions-a">
                    <strong>HTML:</strong> {s.htmlContent}
                  </p>
                  <p className="Admin-trivia-suggestions-q">
                    <strong>Kysymys:</strong> {s.question}
                  </p>
                  <p className="Admin-trivia-suggestions-a">
                    <strong>Vastaus:</strong> {s.answer}
                  </p>
                </>
              ) : (
                <>
                  <p className="Admin-trivia-suggestions-q">
                    <strong>Kysymys:</strong> {s.question}
                  </p>
                  <p className="Admin-trivia-suggestions-a">
                    <strong>Vastaus:</strong> {s.answer}
                  </p>
                </>
              )}

              <MatchDetails s={s} />

              <div className="Admin-trivia-suggestions-actions">
                <button
                  type="button"
                  className="Button Admin-trivia-suggestions-btn-approve"
                  disabled={actingId === s.id}
                  onClick={() => review(s.id, 'approve')}
                >
                  Hyväksy → {s.bankFile || 'pelipankkiin'}
                </button>
                <button
                  type="button"
                  className="Button"
                  disabled={actingId === s.id}
                  onClick={() => review(s.id, 'disapprove')}
                >
                  Hylkää
                </button>
                <button
                  type="button"
                  className="Button Admin-trivia-suggestions-btn-dup"
                  disabled={actingId === s.id}
                  onClick={() => review(s.id, 'duplicate')}
                >
                  Duplikaatti (hyvitys lähettäjälle)
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
